import { validateEmail } from 'api/utils/validateEmail'
import classNames from 'classnames'
import { WizardStepProperties } from 'components/types'
import { ChangeEvent, FocusEvent, useEffect, useRef, useState } from 'react'
import { CustomColorsRef, IconSizes } from 'types/general'

import IconRender from '../IconRender'
import FieldErrorMessage from './FieldErrorMessage'
import { fieldInputClass } from './fieldInputStyles'

const EmailInput = ({
  name,
  label,
  value,
  errorMessage,
  required,
  isFirstInput,
  onChange,
  setIsValid,
  showIcon = true,
  customFieldStyle,
  readOnly,
  highlight,
}: WizardStepProperties) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isFocused, setIsFocused] = useState(false)
  const [isValidField, setIsValidField] = useState(true)

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, type, value } = e.target
    onChange({ name, type, value })
  }

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    setIsFocused(false)
    const normalized = e.target.value.trim().toLowerCase()
    if (normalized !== e.target.value) {
      onChange({ name: e.target.name, type: 'email', value: normalized })
    }

    if ((!required && normalized === '') || validateEmail(normalized)) {
      setIsValidField(true)
      setIsValid?.(true)
    } else {
      setIsValidField(false)
      setIsValid?.(false)
    }
  }

  const handleFocus = () => {
    setIsFocused(true)
  }

  useEffect(() => {
    if (isFirstInput && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isFirstInput])

  return (
    <div className="my-2">
      <label
        htmlFor={name}
        className="block text-imovSecondaryDark font-bold pl-1 mb-0.5 text-md sm:text-base"
      >
        {label}
      </label>
      <div className="relative">
        {showIcon && (
          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
            <IconRender
              icon="regular-envelope"
              size={IconSizes.LG}
              color={
                isFocused || highlight
                  ? CustomColorsRef.PrimaryLight
                  : CustomColorsRef.Gray
              }
            />
          </span>
        )}
        <input
          type="email"
          ref={inputRef}
          id={name}
          name={name}
          value={typeof value === 'string' ? value : ''}
          required={required}
          readOnly={readOnly}
          tabIndex={readOnly ? -1 : undefined}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={readOnly ? undefined : handleChange}
          className={classNames(
            fieldInputClass({ withIcon: showIcon }),
            readOnly && 'cursor-default bg-gray-50 text-gray-600',
            customFieldStyle
          )}
        />
      </div>
      <FieldErrorMessage
        hasError={!isValidField}
        message={errorMessage}
        className="ml-2"
      />
    </div>
  )
}

export default EmailInput
